import React, {useCallback} from "react";
import PropTypes from "prop-types";
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import CustomEvents from "highcharts-custom-events";
import { buildChartTooltip, updateChartTheme } from "../../../utils/helpsers";
import logoSignature from '../../../IB_Signature.png';
import Box from "@mui/material/Box";
import {PieChartStyles as styles} from "./PieChart.styles";
import HighchartsMore from "highcharts/highcharts-more";

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);
HighchartsMore(Highcharts);
CustomEvents(Highcharts);

/**
 * Pie chart using highcharts library.
 * @param {string} title - The title of the chart.
 * @param {string} subtitle - Text to define the subtitle.
 * @param {string} id - A unique name for the chart.
 * @param {array} data - An array data to be rendered in the chart.
 * @param {object} showTable - Data object to build the table.
 * @param {boolean} toggle - A true/false value to display the table.
 * @constructor
 */
const PieChart = ({ title, subtitle = null, id, data, showTable = false, toggle = false }) => {
  let colors = ['#E1D7F0', '#00D7BE', '#773dbd', '#ff5569', '#F5B335', '#F7AEF8'];

  const attachTooltipDescription = useCallback(()=> {
    let infoIcons = document.querySelectorAll('.info-icon');
    Array.from(infoIcons).forEach((el) => {
      let infoIcon = document.getElementById(el.id);
      buildChartTooltip({ infoIcon: infoIcon });
    });
  },[])

  let logoCredit;
  const pieChartData = {
    chart: {
      type: "pie",
      height: 550,
      backgroundColor: '#fff',
      style: {
        fontFamily: ['Titillium Web', 'sans-serif']
      },
      events: {
        load: function () {
          if (this.options.chart.forExport) {
            this.renderer.image(`${logoSignature}`, 10, 530, 160, 20).add();
          }
        },
        render: function (){
          // Building Tooltips
          attachTooltipDescription();
        },
        beforePrint: function () {
          logoCredit = this.renderer.image(`${logoSignature}`, 10, 530, 160, 20).add();
          this.print();
        },
        afterPrint: function () {
          logoCredit.element.remove();
        },
      },
    },
    legend: {
      align: 'center',
      itemStyle: {
        "color": "#333",
      },
      verticalAlign: 'bottom',
      useHTML: true,
      labelFormatter: function () {
        let definition = this.definition,
          labelId = (this.name).replace(/[^A-Z0-9]+/ig, "_") + '_' + id;
        if (definition) {
          return `
            <span class="legend-name">${this.name}
              <span class="info-icon" id="${labelId}" style="font-size: 14px; fill:#773dbd">ⓘ</span>
              <span class="tooltip-desc" id="${labelId}-desc" style="visibility: hidden">${definition}</span>
            </span>
            <span class="legend-value">${this.formatted} / ${this.percentage.toFixed(2)}% </span>`
        } else {
          return this.name + ' ' + this.formatted;
        }
      },
      width: 280,
      padding: 14,
      className: 'tableFormat',
      borderWidth: 1,
      borderColor: '#cdcdcd',
    },
    title: {
      text: '<b>' + title + '</b>',
      style: { "fontSize": "15px" }
    },
    subtitle: {
      text: subtitle,
      style: {
        fontSize: '14px',
      },
    },
    tooltip: {
      followPointer: false,
      formatter: function () {
        return `${this.key}: <b>${this.point.percentage.toFixed(2)}%</b> <br/> Total: <b>${this.point.formatted}</b> `;
      },
      backgroundColor: '#FFF',
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        innerSize: '45%',
        size: 270,
        colors: colors,
        dataLabels: {
          enabled: true,
          connectorShape: 'crookedLine',
          crookDistance: '90%',
          alignTo: 'plotEdges',
          formatter: function () {
            let definition = this.point.definition,
              labelId = (this.key).replace(/[^A-Z0-9]+/ig, "_") + '_' + id;
            if (definition) {
              return `
                      <span class="label-key">${this.key}</span>
                      <span class="info-icon" id="${labelId}" style="font-size: 14px; fill:#773dbd">ⓘ</span>
                      <span class="tooltip-desc" id="${labelId}-desc" style="visibility: hidden">${definition}</span>
                      </br>
                      <span>Total: ${this.point.options.formatted}</span>
                      </br> 
                      <span> ${this.point.percentage.toFixed(2)}% </span>
                      `
            } else {
              return `<span> ${this.key}: ${this.point.options.formatted}</span>`
            }
          },
          style: {
            textOutline: false
          },
          useHTML: true
        },
        showInLegend: showTable,
      }
    },
    exporting: {
      tableCaption: false,
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            {
              text: "Toggle Light/Dark mode",
              onclick: function () {
                updateChartTheme(this);
              }
            },
            {
              separator: true,
            },
            {
              text: "Download PDF document",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictInsights",
                  `Downloaded: PDF in ${title}`,
                ]);
                this.exportChart({
                  type: "application/pdf",
                });
              },
            },
            {
              text: "Download CSV",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictInsights",
                  `Downloaded: CSV in ${title}`,
                ]);
                this.downloadCSV();
              },
            },
            {
              text: "Download XLS",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictInsights",
                  `Downloaded: XLS in ${title}`,
                ]);
                this.downloadXLS();
              },
            },
            {
              separator: true,
            },
            {
              text: "Toggle Data Legend",
              onclick: function () {
                let isLegendVisible = this.userOptions.plotOptions.pie.showInLegend;
                this.update({
                  plotOptions: {
                    pie: {
                      showInLegend: !isLegendVisible,
                      dataLabels: {
                        enabled: isLegendVisible
                      }
                    }
                  }
                })
              }
            },
            {
              text: 'Toggle Raw Data Table',
              onclick: function() {
                if (this.dataTableDiv && this.dataTableDiv.style.display !== 'none') {
                  this.dataTableDiv.style.display = 'none';
                } else {
                  this.viewData();
                  this.dataTableDiv.style.display = '';
                }
              }
            }
          ],
        },
      },
    },
    series: [
      {
        name: "Value",
        data: data
      }
    ]
  }

  return (
    <Box className="PieChart" sx={styles}>
      <div className="PieChart__wrapper">
        <HighchartsReact
            highcharts={Highcharts}
            options={pieChartData}
        />
      </div>
    </Box>
  )
}

PieChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  id: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  table: PropTypes.object,
  toggle: PropTypes.bool,
}
export default React.memo(PieChart);
